import React, { useEffect, useState } from "react";
import { oracleApi } from "@/api/oracleClient";

export default function AlertsPanel({ open, onClose, userId, onChanged, toast }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("unread"); // 'unread' | 'all'

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await oracleApi.alertsList(userId, tab === "unread");
      setAlerts(data?.alerts || []);
    } catch (e) {
      console.error("alerts load failed", e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, userId]);

  if (!open) return null;

  const markRead = async (id) => {
    try {
      await oracleApi.alertRead(id);
      setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, read: true } : a)));
      onChanged?.();
    } catch (e) {
      toast?.("error", e?.response?.data?.detail || e?.message || "Could not mark as read");
    }
  };

  return (
    <div className="oracle-modal-backdrop" onClick={onClose} data-testid="alerts-modal">
      <div className="oracle-modal oracle-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="oracle-modal__head">
          <div className="oracle-modal__title">⚡ ORACLE Watchlist Alerts</div>
          <button className="oracle-modal__x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="oracle-modal__body">
          <div className="oracle-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "unread"}
              className={`oracle-tab ${tab === "unread" ? "is-active" : ""}`}
              onClick={() => setTab("unread")}
              data-testid="alerts-tab-unread"
            >
              Unread
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "all"}
              className={`oracle-tab ${tab === "all" ? "is-active" : ""}`}
              onClick={() => setTab("all")}
              data-testid="alerts-tab-all"
            >
              All
            </button>
          </div>

          {loading && <div className="oracle-empty">Loading alerts…</div>}
          {!loading && alerts.length === 0 && (
            <div className="oracle-empty">
              <div className="oracle-empty__title">No alerts yet</div>
              <div className="oracle-empty__sub">
                Add an asset to your watchlist. When the ORACLE’s verdict on that asset changes,
                you’ll see it here.
              </div>
            </div>
          )}

          <ul className="alerts-list">
            {alerts.map((a) => (
              <li key={a.id} className={`alert-row ${a.read ? "is-read" : ""}`}>
                <div className="alert-row__head">
                  <span className="rc-pill">{(a.mode || "").toUpperCase()}</span>
                  <b>{a.symbol}</b>
                  <span className="alert-row__time">{new Date(a.created_at).toLocaleString()}</span>
                </div>
                <div className="alert-row__body">
                  Verdict changed:{" "}
                  <span className="alert-prev">{a.prev_verdict}</span>
                  {" → "}
                  <span className="alert-new">{a.new_verdict}</span>
                </div>
                {!a.read && (
                  <button
                    type="button"
                    className="oracle-btn oracle-btn--ghost oracle-btn--sm"
                    onClick={() => markRead(a.id)}
                    data-testid={`alert-mark-read-${a.id}`}
                  >
                    Mark as read
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
